import { Chip } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import type { OpsServiceDetail } from '@/api/operations';
import { STATUS_TONE, STATUS_TOOLTIP, CATEGORY_LABEL, CATEGORY_TONE } from './tabs/ops-detail-shared';
import { DetailHeader } from '@/components/detail-header';
import { OpsServiceToggleDialog } from './ops-service-toggle-dialog';

/**
 * Operations service detail identity header — breadcrumbs (Operations › service
 * name) then a title row: `#id` muted prefix + service name (semibold) +
 * StatusPill + category Chip + action buttons (Act as customer, Activate/Deactivate).
 * Mirrors the @ui detail recipe + service-detail-header pattern
 * (Breadcrumbs → h1 row with pill, chips, and actions).
 */
export function OpsServiceDetailHeader({
  data,
  onToggled,
}: {
  data: OpsServiceDetail;
  /** Called after a successful activate/deactivate so the parent can refetch. */
  onToggled?: () => void;
}) {
  const toast = useToast();
  const statusTone = STATUS_TONE[data.status] ?? 'neutral';
  const statusTooltip = STATUS_TOOLTIP[data.status] ?? data.status;
  const category = data.provider?.category;

  const handleToggleSuccess = (action: 'activate' | 'deactivate') => {
    toast.show({
      tone: 'success',
      title: action === 'activate' ? 'Service activated' : 'Service deactivated',
      description:
        action === 'activate'
          ? 'The service has been restored successfully.'
          : 'The service has been soft-terminated and zones parked.',
    });
    onToggled?.();
  };

  return (
    <DetailHeader
      parent={{ label: 'Operations', to: '/operations' }}
      current={data.service_name || `#${data.id}`}
      idPrefix={`#${data.id}`}
      title={data.service_name}
      meta={
        <>
          <StatusPill tone={statusTone} tooltip={statusTooltip}>
            {data.status}
          </StatusPill>
          {category && CATEGORY_LABEL[category] ? (
            <Chip appearance="subtle" tone={CATEGORY_TONE[category] ?? 'neutral'} size="sm">
              {CATEGORY_LABEL[category]}
            </Chip>
          ) : null}
        </>
      }
      actions={<OpsServiceToggleDialog service={data} onSuccess={handleToggleSuccess} />}
    />
  );
}
